import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { 
  MessageCircle, Bot, User, Send, Loader2, 
  RefreshCw, CheckCircle, Clock, XCircle,
  Settings, ChevronLeft, Search, FileText, Trash2, Plus
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { API, useAuth } from '../App';
import AdminLayout from '../components/AdminLayout';
import EditorialPageHeader from '../components/EditorialPageHeader';
import './SupportFinancialsAthletic.css';
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";

const EmbeddedLayout = ({ children }) => (
  <div className="support-athletic space-y-6">
    <EditorialPageHeader
      eyebrow="Operations"
      title="Support"
      description="Manage conversations and help your organization move forward."
      headingId="support-page-title"
    />
    {children}
  </div>
);

const AdminSupport = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    ai_enabled: true,
    welcome_message: 'Hi! How can I help you today?',
    ai_instructions: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCannedDropdown, setShowCannedDropdown] = useState(false);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showManageCanned, setShowManageCanned] = useState(false);
  const [newCannedTitle, setNewCannedTitle] = useState('');
  const [newCannedContent, setNewCannedContent] = useState('');
  const [creatingCanned, setCreatingCanned] = useState(false);
  const messagesEndRef = useRef(null);
  const cannedDropdownRef = useRef(null);

  const orgId = user?.org_id;

  useEffect(() => {
    if (token) {
      fetchConversations();
      if (orgId) {
        fetchSettings();
      }
      fetchCannedResponses();
    }
  }, [token, orgId]);

  // Close canned dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (cannedDropdownRef.current && !cannedDropdownRef.current.contains(e.target)) {
        setShowCannedDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    setLoadError(false);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/support/admin/conversations`, { headers });
      setConversations(res.data);
    } catch (err) {
      setLoadError(true);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchCannedResponses = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/support/admin/canned-responses`, { headers });
      setCannedResponses(res.data);
    } catch (err) {
      // Silently fail - canned responses are optional
    }
  };

  const createCannedResponse = async () => {
    if (!newCannedTitle.trim() || !newCannedContent.trim()) return;
    setCreatingCanned(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/support/admin/canned-responses`, {
        title: newCannedTitle,
        content: newCannedContent
      }, { headers });
      setNewCannedTitle('');
      setNewCannedContent('');
      fetchCannedResponses();
      toast.success('Canned response created');
    } catch (err) {
      toast.error('Failed to create canned response');
    } finally {
      setCreatingCanned(false);
    }
  };

  const deleteCannedResponse = async (responseId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/support/admin/canned-responses/${responseId}`, { headers });
      fetchCannedResponses();
      toast.success('Canned response deleted');
    } catch (err) {
      toast.error('Failed to delete canned response');
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/support/settings/${orgId}`);
      setSettings(res.data);
    } catch (err) {
      console.log('Using default settings');
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setMessagesLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/support/conversations/${conv.conversation_id}`, { headers });
      setMessages(res.data.messages || []);
      
      // Mark as read
      if (conv.unread_count > 0) {
        await axios.put(`${API}/support/admin/conversations/${conv.conversation_id}/read`, {}, { headers });
        setConversations(prev => prev.map(c => 
          c.conversation_id === conv.conversation_id ? { ...c, unread_count: 0 } : c
        ));
      }
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/support/admin/conversations/${selectedConversation.conversation_id}/reply`,
        { content: replyText },
        { headers }
      );
      
      setMessages(prev => [...prev, res.data]);
      setReplyText('');
      toast.success('Reply sent');
      trackAdminWorkflowCompleted("support");
      
      // Refresh conversation list to update timestamps
      fetchConversations();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const updateConversationStatus = async (convId, status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/support/admin/conversations/${convId}/status?status=${status}`, {}, { headers });
      
      setConversations(prev => prev.map(c =>
        c.conversation_id === convId ? { ...c, status } : c
      ));
      
      if (selectedConversation?.conversation_id === convId) {
        setSelectedConversation(prev => ({ ...prev, status }));
      }
      
      toast.success(`Conversation marked as ${status}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/support/settings/${orgId}`, settings, { headers });
      toast.success('Settings saved');
      setShowSettings(false);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.user_name?.toLowerCase().includes(query) ||
      conv.user_email?.toLowerCase().includes(query)
    );
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <Clock className="h-4 w-4 text-[#087f67]" aria-hidden="true" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-[#087f67]" aria-hidden="true" />;
      case 'closed':
        return <XCircle className="h-4 w-4 text-[#718078]" aria-hidden="true" />;
      default:
        return <Clock className="h-4 w-4 text-[#718078]" aria-hidden="true" />;
    }
  };

  if (loading) {
    return (
      <PageLayout title="Support" subtitle="Manage conversations and help your organization move forward." headerEyebrow="Operations">
        <div className="flex min-h-80 items-center justify-center rounded-[28px] border border-[#dfe6df] bg-white" role="status" aria-label="Loading support inbox">
          <Loader2 className="h-8 w-8 animate-spin text-[#087f67]" aria-hidden="true" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Support" subtitle="Manage conversations and help your organization move forward." headerEyebrow="Operations">
      <div className="flex min-h-[600px] overflow-hidden rounded-[28px] border border-[#dfe6df] bg-white shadow-[0_18px_50px_rgba(16,42,39,0.08)] lg:h-[calc(100vh-180px)] lg:min-h-[560px]" data-testid="admin-support">
      {/* Conversations List */}
      <section aria-label="Conversations" className={`${selectedConversation ? 'hidden lg:flex' : 'flex'} min-w-0 flex-1 flex-col lg:w-80 lg:flex-none lg:border-r lg:border-[#dfe6df]`}>
        {/* Header */}
        <div className="border-b border-[#dfe6df] bg-[#fbfcf8] p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-[#102a27]">
              <MessageCircle className="h-5 w-5 text-[#087f67]" aria-hidden="true" />
              Support Inbox
            </h2>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchConversations}
                className="h-10 w-10 rounded-xl text-[#102a27] hover:bg-[#edf3ed] focus-visible:ring-2 focus-visible:ring-[#087f67]"
                aria-label="Refresh conversations"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(true)}
                className="h-10 w-10 rounded-xl text-[#102a27] hover:bg-[#edf3ed] focus-visible:ring-2 focus-visible:ring-[#087f67]"
                aria-label="Open support settings"
              >
                <Settings className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#718078]" aria-hidden="true" />
            <Label htmlFor="support-search" className="sr-only">Search conversations</Label>
            <Input
              id="support-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="h-11 rounded-xl border-[#cbd6cf] bg-white pl-9 text-[#102a27] placeholder:text-[#718078] focus-visible:ring-[#087f67]"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loadError ? (
            <div className="m-5 rounded-2xl border border-[#efc7bd] bg-[#fff7f4] p-6 text-center">
              <XCircle className="mx-auto mb-3 h-8 w-8 text-[#a33d2c]" aria-hidden="true" />
              <p className="font-semibold text-[#102a27]">We couldn't load the inbox.</p>
              <p className="mt-1 text-sm text-[#60706a]">Check your connection and try again.</p>
              <Button onClick={fetchConversations} className="mt-4 rounded-xl bg-[#102a27] text-white hover:bg-[#173b36]">Try again</Button>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-8 text-center text-[#60706a]">
              <MessageCircle className="mx-auto mb-3 h-12 w-12 text-[#9ba89f]" aria-hidden="true" />
              <p className="font-semibold text-[#102a27]">{searchQuery ? 'No matching conversations' : 'No conversations yet'}</p>
              <p className="mt-1 text-sm">{searchQuery ? 'Try a different name or email address.' : 'New support requests will appear here.'}</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <button
                key={conv.conversation_id}
                onClick={() => selectConversation(conv)}
                aria-current={selectedConversation?.conversation_id === conv.conversation_id ? 'true' : undefined}
                className={`w-full border-b border-[#edf1ec] p-4 text-left transition-colors focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#087f67] ${
                  selectedConversation?.conversation_id === conv.conversation_id ? 'bg-[#e9f8f2]' : 'hover:bg-[#f5f7f4]'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <span className="flex-1 truncate font-semibold text-[#102a27]">
                    {conv.user_name || 'Guest'}
                  </span>
                  <div className="flex items-center gap-2">
                    {conv.unread_count > 0 && (
                      <span className="rounded-full bg-[#087f67] px-2 py-0.5 text-xs font-semibold text-white" aria-label={`${conv.unread_count} unread messages`}>
                        {conv.unread_count}
                      </span>
                    )}
                    {getStatusIcon(conv.status)}
                  </div>
                </div>
                {conv.user_email && (
                  <p className="mb-1 truncate text-xs text-[#60706a]">{conv.user_email}</p>
                )}
                <p className="text-xs text-[#718078]">
                  {formatDistanceToNow(new Date(conv.updated_at), { addSuffix: true })}
                  {' • '}{conv.message_count} messages
                </p>
              </button>
            ))
          )}
        </div>
      </section>

      {/* Messages Panel */}
      <section aria-label="Conversation detail" className={`${selectedConversation ? 'flex' : 'hidden lg:flex'} min-w-0 flex-1 flex-col`}>
        {selectedConversation ? (
          <>
            {/* Conversation Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#dfe6df] bg-[#fbfcf8] p-4 sm:p-5">
              <div className="flex min-w-0 items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedConversation(null)}
                  className="h-10 w-10 shrink-0 rounded-xl text-[#102a27] hover:bg-[#edf3ed] focus-visible:ring-2 focus-visible:ring-[#087f67] lg:hidden"
                  aria-label="Back to support inbox"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </Button>
                <div className="min-w-0">
                <h3 className="truncate font-semibold text-[#102a27]">
                  {selectedConversation.user_name || 'Guest'}
                </h3>
                {selectedConversation.user_email && (
                  <p className="truncate text-sm text-[#60706a]">{selectedConversation.user_email}</p>
                )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="conversation-status" className="sr-only">Conversation status</Label>
                <select
                  id="conversation-status"
                  value={selectedConversation.status}
                  onChange={(e) => updateConversationStatus(selectedConversation.conversation_id, e.target.value)}
                  className="min-h-10 rounded-xl border border-[#cbd6cf] bg-white px-3 text-sm font-medium text-[#102a27] focus:outline-none focus:ring-2 focus:ring-[#087f67]"
                >
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 space-y-4 overflow-y-auto bg-[#f5f7f4] p-4 sm:p-6" aria-live="polite" aria-busy={messagesLoading}>
              {messagesLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin text-[#087f67]" aria-label="Loading messages" />
                </div>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.message_id}
                    className={`flex gap-3 ${msg.role === 'user' ? '' : 'flex-row-reverse'}`}
                  >
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      msg.role === 'user' ? 'bg-[#dfe6df]' :
                      msg.role === 'assistant' ? 'bg-[#102a27]' : 'bg-[#087f67]'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="h-4 w-4 text-[#60706a]" aria-hidden="true" />
                      ) : msg.role === 'assistant' ? (
                        <Bot className="h-4 w-4 text-white" aria-hidden="true" />
                      ) : (
                        <User className="h-4 w-4 text-white" aria-hidden="true" />
                      )}
                    </div>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 sm:max-w-[70%] ${
                      msg.role === 'user' 
                        ? 'rounded-tl-sm border border-[#dfe6df] bg-white text-[#102a27]'
                        : msg.role === 'assistant'
                        ? 'rounded-tr-sm bg-[#102a27] text-white'
                        : 'rounded-tr-sm bg-[#087f67] text-white'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`mt-1 text-xs ${msg.role === 'user' ? 'text-[#718078]' : 'opacity-80'}`}>
                        {msg.role === 'assistant' ? 'AI Assistant' : msg.role === 'admin' ? msg.admin_name || 'Admin' : ''}
                        {' • '}{formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="border-t border-[#dfe6df] bg-white p-4 sm:p-5">
              <div className="flex items-end gap-2">
                <div className="relative" ref={cannedDropdownRef}>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowCannedDropdown(!showCannedDropdown)}
                    title="Canned Responses"
                    aria-label="Choose a canned response"
                    aria-expanded={showCannedDropdown}
                    >
                    <FileText className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  {showCannedDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-64 overflow-y-auto">
                      <div className="p-2">
                        {cannedResponses.length === 0 ? (
                          <p className="text-sm text-gray-500 p-2">No canned responses yet</p>
                        ) : (
                          cannedResponses.map((cr) => (
                            <button
                              key={cr.response_id}
                              onClick={() => {
                                setReplyText(prev => prev ? prev + '\n' + cr.content : cr.content);
                                setShowCannedDropdown(false);
                              }}
                              className="w-full text-left p-2 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <p className="text-sm font-medium text-gray-900">{cr.title}</p>
                              <p className="text-xs text-gray-500 truncate">{cr.content}</p>
                            </button>
                          ))
                        )}
                      </div>
                      <div className="border-t border-gray-100 p-2">
                        <button
                          onClick={() => { setShowCannedDropdown(false); setShowManageCanned(true); }}
                          className="text-xs text-blue-500 hover:text-blue-700 font-medium w-full text-left px-2 py-1"
                        >
                          Manage Canned Responses
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                <Label htmlFor="support-reply" className="sr-only">Reply</Label>
                <Textarea
                  id="support-reply"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                  placeholder="Type your reply..."
                  rows={2}
                  className="min-h-11 resize-none rounded-xl border-[#cbd6cf] text-[#102a27] focus-visible:ring-[#087f67]"
                  disabled={sending}
                />
                <p className="mt-1 text-xs text-[#718078]">Enter to send · Shift+Enter for a new line</p>
                </div>
                <Button
                  onClick={handleSendReply}
                  disabled={!replyText.trim() || sending}
                  className="h-11 w-11 shrink-0 rounded-xl bg-[#102a27] p-0 text-white hover:bg-[#173b36] focus-visible:ring-2 focus-visible:ring-[#087f67]"
                  aria-label={sending ? 'Sending reply' : 'Send reply'}
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Send className="h-4 w-4" aria-hidden="true" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-[#60706a]">
            <div className="text-center">
              <MessageCircle className="mx-auto mb-4 h-16 w-16 text-[#b9c4bc]" aria-hidden="true" />
              <p className="text-lg font-semibold text-[#102a27]">Select a conversation</p>
              <p className="text-sm">Choose from the list on the left to view messages</p>
            </div>
          </div>
        )}
      </section>

      {/* Settings Modal (Dialog) */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Support Settings
            </DialogTitle>
            <DialogDescription>Configure AI responses and welcome messages.</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* AI Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="ai-auto-response" className="text-base font-medium">AI Auto-Response</Label>
                <p className="text-sm text-gray-500">
                  Automatically respond to user questions using AI
                </p>
              </div>
              <Switch
                id="ai-auto-response"
                checked={settings.ai_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, ai_enabled: checked })}
              />
            </div>

            {/* Welcome Message */}
            <div>
              <Label htmlFor="welcome-message">Welcome Message</Label>
              <p className="text-sm text-gray-500 mb-2">
                First message users see when opening the chat
              </p>
              <Textarea
                id="welcome-message"
                value={settings.welcome_message}
                onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
                placeholder="Hi! How can I help you today?"
                rows={2}
              />
            </div>

            {/* AI Instructions */}
            {settings.ai_enabled && (
              <div>
                <Label htmlFor="ai-instructions">AI Instructions (Optional)</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Custom instructions for the AI assistant
                </p>
                <Textarea
                  id="ai-instructions"
                  value={settings.ai_instructions}
                  onChange={(e) => setSettings({ ...settings, ai_instructions: e.target.value })}
                  placeholder="e.g., Always mention our upcoming spring season registration..."
                  rows={3}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Canned Responses Dialog */}
      <Dialog open={showManageCanned} onOpenChange={setShowManageCanned}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Manage Canned Responses
            </DialogTitle>
            <DialogDescription>Create and manage reusable reply templates.</DialogDescription>
          </DialogHeader>

          {/* Create New */}
          <div className="space-y-3 border-b border-gray-200 pb-4">
            <Label htmlFor="canned-title" className="sr-only">Response title</Label>
            <Input
              id="canned-title"
              value={newCannedTitle}
              onChange={(e) => setNewCannedTitle(e.target.value)}
              placeholder="Title (e.g., Registration Help)"
            />
            <Label htmlFor="canned-content" className="sr-only">Response content</Label>
            <Textarea
              id="canned-content"
              value={newCannedContent}
              onChange={(e) => setNewCannedContent(e.target.value)}
              placeholder="Response content..."
              rows={2}
            />
            <Button
              onClick={createCannedResponse}
              disabled={!newCannedTitle.trim() || !newCannedContent.trim() || creatingCanned}
              size="sm"
            >
              {creatingCanned ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Response
            </Button>
          </div>

          {/* List */}
          <div className="space-y-2">
            {cannedResponses.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No canned responses yet</p>
            ) : (
              cannedResponses.map((cr) => (
                <div key={cr.response_id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{cr.title}</p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{cr.content}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteCannedResponse(cr.response_id)}
                    className="h-8 w-8 text-red-500 hover:text-red-700 shrink-0 ml-2"
                    aria-label={`Delete ${cr.title}`}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PageLayout>
  );
};

export default AdminSupport;
